import { useEffect, useMemo, useRef, useState } from "react";
import { montarFicha } from "../lib/ficha";
import type { Diferencial, Visita } from "../lib/tipos";

const ID_FRAME_IMPRESSAO = "__coc_frame_impressao";

/**
 * Imprime num iframe **oculto e recém-criado**, não no da pré-visualização.
 *
 * Imprimir o iframe visível parece funcionar, mas o Safari toma a região
 * visível como referência e corta o topo da folha. Por isso o print sai de um
 * iframe de tamanho zero, montado com `srcdoc` e impresso só depois que a logo
 * (data URI) terminou de carregar — imagem pendente desloca o layout.
 */
function imprimirEmFrameOculto(html: string) {
  document.getElementById(ID_FRAME_IMPRESSAO)?.remove();

  const frame = document.createElement("iframe");
  frame.id = ID_FRAME_IMPRESSAO;
  frame.setAttribute(
    "style",
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;",
  );
  frame.srcdoc = html;

  frame.onload = () => {
    const doc = frame.contentDocument;
    const janela = frame.contentWindow;
    if (!doc || !janela) return;

    let jaImprimiu = false;
    const imprimir = () => {
      if (jaImprimiu) return;
      jaImprimiu = true;
      try {
        janela.focus();
        janela.print();
      } catch (e) {
        console.error("[ficha] falha ao imprimir:", e);
      }
      // o frame só sai de cena depois do diálogo; remover antes cancela a impressão
      setTimeout(() => frame.remove(), 60_000);
    };

    const imagens = Array.from(doc.images);
    if (imagens.length === 0) {
      setTimeout(imprimir, 300);
      return;
    }

    let carregadas = 0;
    const conta = () => {
      carregadas++;
      if (carregadas >= imagens.length) setTimeout(imprimir, 400);
    };
    for (const img of imagens) {
      if (img.complete && img.naturalHeight !== 0) conta();
      else {
        img.onload = conta;
        img.onerror = conta;
      }
    }

    // rede de segurança: imprime mesmo se alguma imagem travar
    setTimeout(imprimir, 2500);
  };

  document.body.appendChild(frame);
}

export function FichaImpressao({
  visita,
  catalogo,
  onFechar,
}: {
  visita: Visita;
  /** Todos os diferenciais da escola — a proposta sem visita mostra a lista inteira. */
  catalogo?: Diferencial[];
  onFechar: () => void;
}) {
  const orcamento = visita.tipo === "ORCAMENTO";
  const html = useMemo(() => montarFicha(visita, catalogo), [visita, catalogo]);
  const [imprimindo, setImprimindo] = useState(false);
  const previa = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [onFechar]);

  function imprimir() {
    setImprimindo(true);
    imprimirEmFrameOculto(html);
    setTimeout(() => setImprimindo(false), 3000);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 950, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, maxWidth: 880, width: "100%", maxHeight: "92vh", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,.35)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 24px", borderBottom: "1px solid #e5e5e5", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: "#fafbfc", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, color: "var(--verde)", fontSize: 16 }}>
              Pré-visualização — {orcamento ? "Proposta de Investimento" : "Ficha de Visita"}
            </h3>
            <div style={{ fontSize: 12, color: "var(--texto-suave)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {visita.nomeAluno} · Protocolo {visita.id.slice(0, 8).toUpperCase()}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button className="botao" onClick={imprimir} disabled={imprimindo}>
              🖨️ {imprimindo ? "Preparando..." : orcamento ? "Salvar em PDF" : "Imprimir"}
            </button>
            <button className="botao secundario" onClick={onFechar}>Fechar</button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "24px 32px", background: "#e8eaed" }}>
          {/* iframe só de leitura: a impressão nunca sai daqui */}
          <iframe
            ref={previa}
            title="Pré-visualização da ficha"
            srcDoc={html}
            style={{ width: "100%", minHeight: 900, border: "none", background: "#fff", borderRadius: 4, boxShadow: "0 2px 12px rgba(0,0,0,.12)", display: "block" }}
          />
        </div>

        <div style={{ padding: "10px 24px", borderTop: "1px solid #e5e5e5", fontSize: 12, color: "var(--texto-suave)", background: "#fafbfc" }}>
          {orcamento ? (
            <>
              No diálogo que abrir, escolha <strong>Destino → Salvar como PDF</strong> e desmarque{" "}
              <strong>"Imprimir cabeçalhos e rodapés"</strong>. Depois é só anexar o arquivo no WhatsApp da família.
            </>
          ) : (
            <>
              No diálogo de impressão, desmarque <strong>"Imprimir cabeçalhos e rodapés"</strong> — o cabeçalho do navegador empurra o conteúdo e pode cortar o topo da ficha.
            </>
          )}
        </div>
      </div>
    </div>
  );
}
